import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faFire, faUsers, faCogs, faBolt, faBuilding, faFirstAid,
  faHardHat, faClipboardCheck, faLink, faRobot, faMobileScreen,
  faShieldHalved, faWandMagicSparkles, faCircleCheck,
  faSpinner, faHourglassHalf, faCompass, faBell, faCube, faStar,
  faMapLocationDot, faCameraRetro, faLayerGroup, faEnvelope,
} from '@fortawesome/free-solid-svg-icons';
import './RoadmapPage.css';

const PHASES = [
  { key: 'live',   label: 'Shipping now',   sub: 'Live in production',      color: '#0d9488', icon: faCircleCheck },
  { key: 'next',   label: 'Landing next',   sub: 'In active development',   color: '#0c437c', icon: faSpinner },
  { key: 'future', label: 'On the roadmap', sub: 'Planned for later this year', color: '#7c5cff', icon: faHourglassHalf },
  { key: 'vision', label: 'Exploring',      sub: 'Research and design',     color: '#b45309', icon: faCompass },
];

const CATEGORIES = [
  { key: 'all',        label: 'All areas',           icon: faLayerGroup },
  { key: 'roof',       label: 'Roof safety',         icon: faHardHat },
  { key: 'fire',       label: 'Fire safety',         icon: faFire },
  { key: 'staff',      label: 'Staff safety',        icon: faUsers },
  { key: 'equipment',  label: 'Equipment',           icon: faCogs },
  { key: 'electrical', label: 'Electrical',          icon: faBolt },
  { key: 'building',   label: 'Building',            icon: faBuilding },
  { key: 'emergency',  label: 'Emergency',           icon: faFirstAid },
  { key: 'platform',   label: 'Platform & AI',       icon: faWandMagicSparkles },
];

const ITEMS = [
  { phase: 'live', category: 'roof', icon: faHardHat, title: 'Roof Safety Module', body: 'Anchor points, cradles, abseil systems, ladders, handrails, static lines, walkways — each with full testing, drawing hotspots and compliance tracking.' },
  { phase: 'live', category: 'platform', icon: faMapLocationDot, title: 'Interactive drawings', body: 'Upload floor plans, drop pins per asset, and jump from a pin straight into its test history.' },
  { phase: 'live', category: 'platform', icon: faClipboardCheck, title: 'Hydrajaws integration', body: 'Live test results stream in via API and sync to the matching asset automatically.' },
  { phase: 'live', category: 'platform', icon: faWandMagicSparkles, title: 'Asset Generation v2', body: 'Onboard hundreds of assets from a single PDF in minutes with auto-generated property and asset IDs.' },

  { phase: 'next', category: 'fire', icon: faFire, title: 'Fire Safety Module', body: 'Extinguishers, alarms, sprinklers, emergency lights, fire doors, smoke and heat detectors — with BS 5839 certification workflows.' },
  { phase: 'next', category: 'platform', icon: faBell, title: 'Smart notifications', body: 'Tiered email and in-app alerts driven by risk, not just due dates. Routed to the right person automatically.' },
  { phase: 'next', category: 'platform', icon: faCameraRetro, title: 'Photographic evidence', body: 'Attach and version photos per inspection — before-and-after comparisons for remediated defects.' },
  { phase: 'next', category: 'platform', icon: faShieldHalved, title: 'Audit-ready PDF reports', body: 'Branded exports for insurers and regulators. Include drawing overlays, status snapshots, and full test history.' },

  { phase: 'future', category: 'equipment', icon: faCogs, title: 'Equipment Safety Module', body: 'Lifting equipment, PUWER machinery, pressure systems. LOLER schedules and operator sign-offs built in.' },
  { phase: 'future', category: 'electrical', icon: faBolt, title: 'Electrical Safety Module', body: 'Fixed wiring, PAT testing, distribution boards, emergency shut-offs — tracked against BS 7671 cycles.' },
  { phase: 'future', category: 'staff', icon: faUsers, title: 'Staff Safety Module', body: 'PPE registers, training matrices, working-at-height authorisations and RAMS sign-off routing.' },
  { phase: 'future', category: 'building', icon: faBuilding, title: 'Building Maintenance Module', body: 'Planned preventative maintenance calendars with SLA tracking per contractor, tied back to building zones.' },
  { phase: 'future', category: 'emergency', icon: faFirstAid, title: 'Emergency Preparedness', body: 'Evacuation plans, first-aid station audits, fire drill logs, incident reports with timeline reconstruction.' },
  { phase: 'future', category: 'platform', icon: faMobileScreen, title: 'Native mobile app', body: 'iOS and Android apps for on-site inspectors. Offline-first with background sync on reconnect.' },
  { phase: 'future', category: 'platform', icon: faLink, title: 'Partner integrations', body: 'Planon, IBM TRIRIGA, Archibus, SAP PM — bi-directional asset sync so CloudSafe is the single source of safety truth.' },

  { phase: 'vision', category: 'platform', icon: faRobot, title: 'AI Compliance Copilot', body: 'Ask questions in plain English across every asset, test and drawing. "Which ladders at UCL are due in the next 60 days?"' },
  { phase: 'vision', category: 'platform', icon: faCube, title: '3D Building Twin', body: 'Full interactive 3D walkthrough of each property. Pin assets in 3D space, see risk hotspots from every angle.' },
  { phase: 'vision', category: 'platform', icon: faStar, title: 'Predictive failure detection', body: 'A model trained on your testing history flags equipment statistically likely to fail before it does.' },
];

export default function RoadmapPage() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');

  const filtered = useMemo(() => (
    activeCategory === 'all' ? ITEMS : ITEMS.filter(i => i.category === activeCategory)
  ), [activeCategory]);

  const counts = useMemo(() => {
    const c = {};
    PHASES.forEach(p => { c[p.key] = filtered.filter(i => i.phase === p.key).length; });
    return c;
  }, [filtered]);

  return (
    <div className="rmp">
      <div className="rmp-shell">
        {/* ── Top bar ───────────────────────────── */}
        <div className="rmp-topbar">
          <button className="rmp-back" onClick={() => navigate(-1)} type="button">
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>Back</span>
          </button>
          <span className="rmp-wordmark">CloudSafe · Roadmap</span>
        </div>

        {/* ── Hero ──────────────────────────────── */}
        <header className="rmp-hero">
          <div className="rmp-hero-tag">
            <span className="rmp-hero-tag-dot" />
            Updated monthly
          </div>
          <h1 className="rmp-hero-title">
            What we're building next for
            <br />
            <span className="rmp-hero-title-accent">property safety compliance.</span>
          </h1>
          <p className="rmp-hero-lede">
            CloudSafe is shipping new workflows every month. Here's a clear view of what's live,
            what's landing next, and where we're headed — all in one place.
          </p>

          <dl className="rmp-stats">
            {PHASES.map(p => (
              <div key={p.key} className="rmp-stat">
                <dt className="rmp-stat-label" style={{ color: p.color }}>{p.label}</dt>
                <dd className="rmp-stat-value">{counts[p.key]}</dd>
              </div>
            ))}
          </dl>
        </header>

        {/* ── Filters ───────────────────────────── */}
        <div className="rmp-filters-wrap">
          <span className="rmp-filters-label">Filter by area</span>
          <div className="rmp-filters" role="tablist" aria-label="Filter roadmap by area">
            {CATEGORIES.map(cat => (
              <button
                key={cat.key}
                role="tab"
                aria-selected={activeCategory === cat.key}
                className={`rmp-filter ${activeCategory === cat.key ? 'is-active' : ''}`}
                onClick={() => setActiveCategory(cat.key)}
                type="button"
              >
                <FontAwesomeIcon icon={cat.icon} />
                <span>{cat.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ── Timeline ──────────────────────────── */}
        <div className="rmp-timeline">
          {PHASES.map((phase, phaseIdx) => {
            const items = filtered.filter(i => i.phase === phase.key);
            return (
              <section
                key={phase.key}
                className="rmp-phase"
                style={{ '--phase-color': phase.color }}
              >
                <div className="rmp-phase-rail">
                  <div className="rmp-phase-node">
                    <FontAwesomeIcon icon={phase.icon} />
                  </div>
                  {phaseIdx < PHASES.length - 1 && <div className="rmp-phase-line" />}
                </div>

                <div className="rmp-phase-content">
                  <header className="rmp-phase-head">
                    <h2 className="rmp-phase-title">{phase.label}</h2>
                    <p className="rmp-phase-sub">{phase.sub}</p>
                  </header>

                  {items.length === 0 ? (
                    <div className="rmp-phase-empty">
                      Nothing in this phase for the selected area.
                    </div>
                  ) : (
                    <div className="rmp-cards">
                      {items.map((item, i) => (
                        <article
                          key={`${phase.key}-${i}`}
                          className="rmp-card"
                          style={{ animationDelay: `${i * 60}ms` }}
                        >
                          <div className="rmp-card-ico">
                            <FontAwesomeIcon icon={item.icon} />
                          </div>
                          <div className="rmp-card-body">
                            <span className="rmp-card-cat">
                              {CATEGORIES.find(c => c.key === item.category)?.label || item.category}
                            </span>
                            <h3 className="rmp-card-title">{item.title}</h3>
                            <p className="rmp-card-copy">{item.body}</p>
                          </div>
                        </article>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            );
          })}
        </div>

        {/* ── Footer CTA ────────────────────────── */}
        <footer className="rmp-cta">
          <div className="rmp-cta-text">
            <h3>Missing something critical?</h3>
            <p>Tell us what you need next. We prioritise based on customer demand.</p>
          </div>
          <a className="rmp-cta-btn" href="mailto:roadmap@cloud-safe.uk">
            <FontAwesomeIcon icon={faEnvelope} />
            <span>Request a feature</span>
          </a>
        </footer>
      </div>
    </div>
  );
}
